import React from 'react';
import './campaign.css';

/**
 * Holiday campaign themes: colour pair + ink (text on the coloured area) + a small inline SVG motif.
 * Keep the colours in step with api MailLayout::CAMPAIGN_THEMES (email uses colours only, no SVG).
 */
export const CAMPAIGN_THEMES = {
    default: { c1: '#2357ff', c2: '#18c2e8', ink: '#ffffff', label: 'Default' },
    christmas: { c1: '#c62828', c2: '#1b5e20', ink: '#ffffff', label: 'Christmas' },
    blackfriday: { c1: '#111111', c2: '#3a3a3a', ink: '#e8c25a', label: 'Black Friday' },
    easter: { c1: '#f3c4dc', c2: '#bfe3d8', ink: '#3d2b4f', code: '#8a4f7d', label: 'Easter' },
    halloween: { c1: '#ff7a00', c2: '#1a1a1a', ink: '#ffffff', label: 'Halloween' },
    newyear: { c1: '#c9971c', c2: '#f1d27a', ink: '#2f2200', code: '#8a6512', label: 'New Year' },
    valentines: { c1: '#d81b60', c2: '#ff8fb1', ink: '#ffffff', label: "Valentine's Day" },
    springfestival: { c1: '#c8102e', c2: '#e8a317', ink: '#fff4cc', label: 'Chinese New Year' },
    midautumn: { c1: '#13235b', c2: '#2d4a9a', ink: '#f3d98b', label: 'Mid-Autumn Festival' },
    dragonboat: { c1: '#1b7f4d', c2: '#56b870', ink: '#ffffff', label: 'Dragon Boat Festival' },
    labour: { c1: '#1e5bd8', c2: '#5aa9ff', ink: '#ffffff', label: 'Labour Day' },
    children: { c1: '#ff5a5f', c2: '#3b82f6', ink: '#ffffff', label: "Children's Day" },
    double11: { c1: '#e02020', c2: '#ff6a3d', ink: '#ffffff', label: '11.11' },
    midyear: { c1: '#e1251b', c2: '#ff9a3c', ink: '#ffffff', label: '618' },
};

export const themeOf = (key) => CAMPAIGN_THEMES[key] || CAMPAIGN_THEMES.default;

/** CSS variables for a themed element. */
export const themeVars = (key) => {
    const th = themeOf(key);
    return { '--cp1': th.c1, '--cp2': th.c2, '--cpink': th.ink, '--cpcode': th.code || th.c1 };
};

const S = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' };

/** Simple line motifs (48×48), drawn in currentColor. */
const MOTIFS = {
    christmas: ( // snowflake
        <g {...S}>
            {[0, 60, 120].map((r) => (
                <g key={r} transform={`rotate(${r} 24 24)`}>
                    <path d="M24 6v36" />
                    <path d="M24 12l-4-4M24 12l4-4M24 36l-4 4M24 36l4 4" />
                </g>
            ))}
        </g>
    ),
    blackfriday: ( // sparkle
        <g {...S}>
            <path d="M24 6c1.5 9 4 12 13 14-9 2-11.5 5-13 14-1.5-9-4-12-13-14 9-2 11.5-5 13-14z" />
            <path d="M38 34l1 3 3 1-3 1-1 3-1-3-3-1 3-1z" />
        </g>
    ),
    easter: ( // egg
        <g {...S}>
            <path d="M24 6c-8 0-13 13-13 21a13 13 0 0 0 26 0C37 19 32 6 24 6z" />
            <path d="M12 26l4-3 4 3 4-3 4 3 4-3 4 3" />
        </g>
    ),
    halloween: ( // pumpkin
        <g {...S}>
            <path d="M24 14c-3-1-5-4-4-7" />
            <ellipse cx="24" cy="28" rx="15" ry="12" />
            <path d="M24 16c-4 3-5 8-5 12s1 9 5 12M24 16c4 3 5 8 5 12s-1 9-5 12" />
        </g>
    ),
    newyear: ( // firework burst
        <g {...S}>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((r) => (
                <path key={r} transform={`rotate(${r} 24 24)`} d="M24 6v8" />
            ))}
            <circle cx="24" cy="24" r="3" />
        </g>
    ),
    valentines: ( // heart
        <g {...S}>
            <path d="M24 39S8 29 8 18a8 8 0 0 1 16-3 8 8 0 0 1 16 3c0 11-16 21-16 21z" />
        </g>
    ),
    springfestival: ( // lantern
        <g {...S}>
            <path d="M24 4v4M18 8h12M18 38h12M24 38v6" />
            <path d="M18 8c-6 3-9 8-9 15s3 12 9 15h12c6-3 9-8 9-15s-3-12-9-15z" />
            <path d="M24 8c-4 4-5 9-5 15s1 11 5 15M24 8c4 4 5 9 5 15s-1 11-5 15" />
        </g>
    ),
    midautumn: ( // crescent moon + star
        <g {...S}>
            <path d="M30 8a16 16 0 1 0 10 26A13 13 0 0 1 30 8z" />
            <path d="M38 10l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
        </g>
    ),
    dragonboat: ( // waves + boat
        <g {...S}>
            <path d="M8 26h30l-4 6H12z" />
            <path d="M38 26c2-2 2-6 0-8" />
            <path d="M6 38c3 0 3-2 6-2s3 2 6 2 3-2 6-2 3 2 6 2 3-2 6-2" />
        </g>
    ),
    labour: ( // gear
        <g {...S}>
            <circle cx="24" cy="24" r="6" />
            <path d="M24 8v5M24 35v5M8 24h5M35 24h5M12.7 12.7l3.5 3.5M31.8 31.8l3.5 3.5M12.7 35.3l3.5-3.5M31.8 16.2l3.5-3.5" />
            <circle cx="24" cy="24" r="12" />
        </g>
    ),
    children: ( // balloons
        <g {...S}>
            <ellipse cx="18" cy="16" rx="7" ry="8.5" />
            <ellipse cx="31" cy="19" rx="6" ry="7.5" />
            <path d="M18 24.5c0 6 3 9 4 18M31 26.5c0 5-3 8-5 16" />
        </g>
    ),
    double11: ( // shopping bag
        <g {...S}>
            <path d="M10 16h28l-2 24H12z" />
            <path d="M18 20v-6a6 6 0 0 1 12 0v6" />
        </g>
    ),
    midyear: ( // tag
        <g {...S}>
            <path d="M8 24V10h14l18 18-14 14z" />
            <circle cx="15" cy="17" r="2.5" />
        </g>
    ),
};

/** Corner motif for a theme (null for the default theme). */
export const CampaignMotif = ({ theme, size = 44, className = '' }) => {
    const m = MOTIFS[theme];
    if (!m) return null;
    return (
        <svg className={`cpm ${className}`} width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
            {m}
        </svg>
    );
};

export default CAMPAIGN_THEMES;
